/**
 * Сравнение черновика геометрии и метаданных для индикатора «есть несохранённые изменения».
 * Координаты округляются до 6 знаков, чтобы уменьшить ложные срабатывания из-за float.
 */

function roundCoord(n: number): number {
  return Math.round(n * 1e6) / 1e6;
}

function roundDeep(value: unknown): unknown {
  if (value === null || value === undefined) return value;
  if (typeof value === "number" && Number.isFinite(value)) return roundCoord(value);
  if (Array.isArray(value)) return value.map(roundDeep);
  if (typeof value === "object") {
    const o = value as Record<string, unknown>;
    const keys = Object.keys(o).sort();
    const out: Record<string, unknown> = {};
    for (const k of keys) {
      out[k] = roundDeep(o[k]);
    }
    return out;
  }
  return value;
}

export function stableGeoJsonString(raw: unknown): string {
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    return JSON.stringify(roundDeep(parsed));
  } catch {
    return "";
  }
}

export function isGeoJsonContentDirty(current: unknown, original: unknown): boolean {
  if (!original && !current) return false;
  return stableGeoJsonString(current) !== stableGeoJsonString(original);
}

export interface DrawingDirtyInput {
  isEditing: boolean;
  /** GeoJSON Feature или объект с полем geometry */
  currentGeo: unknown;
  originalGeo: unknown;
  name: string;
  description: string;
  color: string;
  opacity: number;
  originalName?: string;
  originalDescription?: string;
  originalColor?: string;
  originalOpacity?: number;
}

/**
 * Есть ли несохранённые изменения (контур и/или форма).
 */
export function computeDrawingDirty(input: DrawingDirtyInput): boolean {
  const {
    isEditing,
    currentGeo,
    originalGeo,
    name,
    description,
    color,
    opacity,
    originalName = "",
    originalDescription = "",
    originalColor = "#3B82F6",
    originalOpacity = 50,
  } = input;

  if (isEditing) {
    if (isGeoJsonContentDirty(currentGeo, originalGeo)) return true;
    if (name.trim() !== (originalName ?? "").trim()) return true;
    if (description.trim() !== (originalDescription ?? "").trim()) return true;
    if (color !== originalColor) return true;
    if (opacity !== originalOpacity) return true;
    return false;
  }

  const hasGeo =
    currentGeo != null &&
    (typeof currentGeo === "object" || typeof currentGeo === "string");
  if (hasGeo) {
    try {
      const g = typeof currentGeo === "string" ? JSON.parse(currentGeo) : currentGeo;
      const geom = (g as { geometry?: unknown })?.geometry ?? g;
      if (geom != null && typeof geom === "object") return true;
    } catch {
      return true;
    }
  }
  if (name.trim() !== "") return true;
  if (description.trim() !== "") return true;
  return false;
}
